import * as _ from "lodash";
import { inject, observer } from "mobx-react";
import React from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { toLuxon } from "../../shared/dateTimeUtils";
import packageInfo from "../../../package.json";
import config from "../../config";
import Loader from "../../shared/Loader/Loader";
import ProgressBar from "../../shared/ProgressBar";
import Toggle from "../../shared/Toggle/Toggle";
import Widget from "../Widget";
import { MODULE_ID } from "../../shared/moduleIds";
import "./CreateStatement.scss";
import { generateYScale } from "./MonitorUtils";
import tinyColor from "tinycolor2";
import { reactSelectTheme } from "../../shared/colorScheme";
import formatNumber from "../../shared/formatNumber";
import { parse, isValid, format } from "date-fns";
import reactSwal from "../../shared/Swal";

class CreateStatement extends Widget {
  chartVerticalSpace = 190;
  maxCanvasWidth = 1400;
  topMargin = 20;
  secondsPerCanvas = 6;

  constructor(props) {
    super(props);
    if (!props.store?.monitorStore?.snippetStartTime) {
      this.isInvalid = true;
      return;
    }
    this.handleSelectChange = this.handleSelectChange.bind(this);
    this.store = props.store.monitorStore;
    const { PR, QRS, QT, QTc } = this.store;
    this.backup = { PR, QRS, QT, QTc };
    this.store.liveMode = false;
    this.store.selectedDiagnoses = [];

    if (!config.pm.training) {
      this.store.validateSnippetParameters(props.store.patient).catch((err) => {
        reactSwal
          .fire({
            title: "Pre Verification Error",
            text: "Cannot create snippet for this patient.",
            icon: "error",
            showCancelButton: true,
            cancelButtonText: "details",
          })
          .then((result) => {
            if (result.dismiss === reactSwal.DismissReason.cancel) {
              console.error(err);
              reactSwal.fire({
                title: err.title,
                text: `${err.description || ""}
                                        ${err.details || ""}`,
              });
            }
          });
        this.backToTools();
      });
    } else {
      console.log("In training mode, skipping snippet pre validation.");
    }

    props.store.hideMainNav = true;
    if (this.store.boundaries) {
      this.store.toolsChartEndTime = this.store.chartEndTime;
      this.store.chartEndTime = this.store.snippetEndTime;
      this.fetchMeasurements();
    } else {
      this.backToTools();
    }

    let hr = this.store.getHeartRate();

    if (hr === undefined) {
      hr = this.store.discretes.find((d) => d.vitalType === "HR");
      this.store.HR = hr?.values[0];
    } else if (hr) {
      this.store.HR = hr.value;
    }

    props.store.getPref("pm.snippet.allLeads").then((allLeads) => {
      this.store.isAllLeads = this.store.isAllLeadsRender = allLeads;
    });

    if (!this.store.measurementMode) {
      Object.assign(this.store, {
        PR: undefined,
        QRS: undefined,
        QT: undefined,
      });
      if (config.pm.qtc) {
        Object.assign(this.store, {
          QTc: undefined,
        });
      }
    }
    this.setupDiscretes();

    // set default for non-worklist snippet
    if (!this.isSnippetWorklist) {
      let defaultId = config.pm.defaultSnippetDocTypeId;
      if (defaultId || defaultId === 0) {
        let types = this.store.snippetDocumentTypes;
        this.store.selectedDocTypeOption = types.find(
          (t) => t.value === defaultId.toString(),
        );
      }
    }

    this.preview = this.preview.bind(this);
    this.save = this.save.bind(this);
    this.saveComplete = this.saveComplete.bind(this);
    this.cancel = this.cancel.bind(this);
    this.pixelsPerSecond = this.maxCanvasWidth / this.secondsPerCanvas;
    this.store.pixelsPerSecond = this.pixelsPerSecond;
    this.chartHeight = this.pixelsPerSecond / this.store.chartAspect;
    this.centerCharts = this.centerCharts.bind(this);
    this.toggleAllLeads = this.toggleAllLeads.bind(this);
    this.descriptionChange = this.descriptionChange.bind(this);
    this.store.showStatementCodes =
      !!props.store.serverPrefs?.pm.snippetsShowStatementCodes ?? true;
    this.store.enableFreeText =
      !!props.store.serverPrefs?.pm?.snippetsEnableDescriptionFreeText ??
      config.pm.snippetsEnableDescriptionFreeText;
    this.store.fetchDiagnoses();
    this.changeMeasurement = this.changeMeasurement.bind(this);
  }

  get editMeasurements() {
    return (
      this.props.store.serverPrefs?.pm?.snippetsEnableEditableMeasurementText ??
      false
    );
  }

  // TODO: Move this to a store for snippets
  fetchMeasurements = () => {
    this.store.chartEndTime = this.store.snippetEndTime;
    this.store.loadingSnippet = true;
    let failures = 0;
    let failingAtSecond = null;
    let lastSecondsLoaded = 0;
    const intId = setInterval(() => {
      const res = this.store.updateMeasurementBuffers({
        pixelWidth: this.chartLengthSeconds * this.pixelsPerSecond,
        bufferSeconds: 1,
      });
      if (res?.filled) {
        clearInterval(intId);
        this.store.loadingSnippet = false;
        this.store.snippetLoaded = true;
        return;
      }
      if (this.secondsLoaded === lastSecondsLoaded) {
        if (failingAtSecond === this.secondsLoaded) failures++;
        else failures = 1;
        failingAtSecond = this.secondsLoaded;
        if (failures > 10) {
          // arbitrary number of seconds to wait
          reactSwal
            .fire({
              title: "Error Fetching Data",
              text: "Please try again.",
              icon: "error",
              cancelButtonText: "continue",
              confirmButtonText: "Back",
            })
            .then((selection) => {
              if (selection.value) {
                this.backToTools();
              }
            });
          this.store.loadingSnippet = false;
          this.store.snippetLoaded = true;
          clearInterval(intId);
        }
      }
      lastSecondsLoaded = this.secondsLoaded;
    }, 1000);
    this.disposers.add(() => clearInterval(intId));
  };

  componentDidMount() {
    if (this.isInvalid) return this.backToTools();
    super.componentDidMount();
    this.props.store.snippets = undefined;
    if (this.store && this.store.snippetEndTime)
      this.canvasRefs = Array(this.canvasCountX)
        .fill(null)
        .map(() => []);
    else console.error("componentDidMount: this.canvasCountX is undefined");
  }

  componentDidUpdate() {
    if (this.isInvalid) return;
    const { canvasCountX, canvasCountY } = this;
    const { snippetLoaded, isAllLeads } = this.store;
    const newChartState = {
      canvasCountX,
      canvasCountY,
      isAllLeads,
      snippetLoaded,
    };
    const stateChanged = !_.isEqual(newChartState, this.chartState);
    if (
      snippetLoaded &&
      stateChanged &&
      this.canvasRefs[canvasCountX - 1][canvasCountY - 1]
    ) {
      this.chartState = newChartState;
      this.renderCharts();
    }
  }

  disposers = new Set();
  componentWillUnmount() {
    if (this.isInvalid) return;
    [...this.disposers].forEach((d) => d());
    super.componentWillUnmount();
    cancelAnimationFrame(this.raf);
    if (this.store) this.store.pdf = null;
  }

  get isSnippetWorklist() {
    return this.store.isSnippetWorklist;
  }
  get isBaselineWorklist() {
    return this.store.isBaselineWorklist;
  }

  get chartLengthSeconds() {
    if (!this.store) return null;
    const endDateTime = this.store.snippetEndTime;
    const startDateTime = this.store.snippetStartTime;
    return endDateTime.diff(startDateTime, "seconds").as("seconds");
  }

  get fullChartWidth() {
    return this.chartLengthSeconds * this.pixelsPerSecond;
  }

  get canvasCountX() {
    return Math.ceil(this.fullChartWidth / this.maxCanvasWidth);
  }

  get canvasCountY() {
    return this.store.isAllLeadsRender
      ? Math.ceil(this.store.config.waveGroups.slice().length / 8)
      : 1;
  }

  get secondsLoaded() {
    return (
      this.store.measurements?.data?.filter(
        (d) =>
          d.dateTime >= this.store.snippetStartTime &&
          d.dateTime <= this.store.snippetEndTime,
      ).length || 0
    );
  }

  get intervalPlaceholder() {
    return this.props.store.serverPrefs?.pm
      ?.snippetsShowMutedMeasurementTextAsDashes
      ? "--"
      : "0";
  }

  canvasRefs = [];

  render() {
    if (this.isInvalid) return null;
    if (this.saved) return null;
    let fullChartWidth = this.fullChartWidth;
    let maxCanvasWidth = this.maxCanvasWidth;
    const selectProps = {
      options: this.store.diagnoses.slice(),
      isMulti: true,
      className: "Select",
      classNamePrefix: "Select",
      onChange: this.handleSelectChange,
      value: this.store.selectedDiagnoses.slice(),
      theme: reactSelectTheme({ darkMode: this.props.store.darkMode }),
      ref: (ref) => (this.descriptionSelect = ref),
    };

    if (this.store.pdf) {
      return (
        <div className="widget create-statement">
          <iframe className="pdf" src={this.store.pdf + "#toolbar=0"} />
          <div className="actions">
            <button className="btn" onClick={this.save}>
              Save
            </button>
            <button
              className="btn secondary"
              onClick={() => (this.store.pdf = null)}
            >
              Back
            </button>
          </div>
          <canvas
            className="snapshot"
            ref={(r) => (this.refSnapshot = r)}
            height="400"
            width="400"
          />
        </div>
      );
    }

    return (
      <div className="widget create-statement">
        <h2>
          {this.store.isBaselineWorklist ? (
            _.get(this.props.store, "documentLabels.snippetDocumentTypes[1]")
          ) : this.store.isSnippetWorklist ? (
            _.get(this.props.store, "documentLabels.snippetDocumentTypes[2]")
          ) : _.get(this.store.documentLabels, "showsDocumentTypePicker") ? (
            <Select
              options={this.store.snippetDocumentTypes}
              className="snippet-doc-type-select"
              onChange={(option) => (this.store.selectedDocTypeOption = option)}
              value={this.store.selectedDocTypeOption}
              theme={reactSelectTheme({ darkMode: this.props.store.darkMode })}
            />
          ) : (
            _.get(this.props.store, "documentLabels.snippetDocumentTypes[3]")
          )}{" "}
          Description
        </h2>
        {this.props.store.serverPrefs?.pm?.snippetsRequireDescription && (
          <label className="required">(Required)</label>
        )}
        {this.store.diagnoses.length ? (
          this.store.enableFreeText ? (
            <CreatableSelect
              {...selectProps}
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  boxShadow:
                    this.selectError && state.isFocused && "0 0 0 1px #c83232",
                  border: this.selectError && state.isFocused && "#c83232;",
                }),
              }}
            />
          ) : (
            <Select
              {...selectProps}
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  boxShadow:
                    this.selectError && state.isFocused && "0 0 0 1px #c83232",
                  border: this.selectError && state.isFocused && "#c83232;",
                }),
              }}
            />
          )
        ) : null}

        <h3>Measurements</h3>
        <div className="fieldset">
          <label>
            HR:
            <input
              defaultValue={this.store.HR}
              onChange={this.changeMeasurement("HR")}
              readOnly={
                !this.props.store.serverPrefs?.pm
                  ?.snippetsEnableHrEditableMeasurementText
              }
            />
          </label>

          <>
            <IntervalInput interval="PR" parent={this} />
            <IntervalInput interval="QRS" parent={this} />
            <IntervalInput interval="QT" parent={this} />
            {config.pm.qtc ? (
              <label>
                QTc <span className="unit">(s)</span>:
                <input
                  defaultValue={formatNumber(this.store.QTc)}
                  type="number"
                  onChange={this.changeMeasurement("QTc")}
                  readOnly={true}
                  placeholder="--"
                />
              </label>
            ) : null}
          </>

          {this.store.discretesOnSnippet.map((d) => (
            <label key={d.title} className="discrete">
              {d.title}:
              <input
                defaultValue={d.value}
                onChange={(e) => {
                  d.value = e.target.value;
                }}
                readOnly={true}
              />
            </label>
          ))}
        </div>
        <div className="snippet-meta">
          <Toggle
            onChange={this.toggleAllLeads}
            label="All Leads"
            checked={!!this.store.isAllLeads}
            disabled={this.toggleAllLeadsDisabled}
          />
          <div className="leads">{_.round(this.store.seconds.value, 1)}s</div>
          <div className="date">
            {this.date(this.store.snippetStartTime)} (
            {this.time(this.store.snippetStartTime)}–
            {this.time(this.store.snippetEndTime)})
          </div>
        </div>

        {this.store.loadingSnippet ? (
          <ProgressBar
            total={this.store.seconds.value + 1}
            complete={Math.min(this.store.seconds.value, this.secondsLoaded)}
            label="Gathering Data (%1 of total %2)"
          />
        ) : (
          this.store.snippetLoaded && (
            <div className="paper-charts" ref={(r) => (this.refCharts = r)}>
              {(() => {
                return (
                  <div className="waveform">
                    {this.canvasCountX
                      ? _.flatten(
                          Array(this.canvasCountX)
                            .fill()
                            .map((u, columnIndex) => {
                              return (
                                <div
                                  className="canvas-column"
                                  key={columnIndex}
                                >
                                  {Array(this.canvasCountY)
                                    .fill()
                                    .map((u, rowIndex) => {
                                      const lastCanvasRow =
                                        rowIndex === this.canvasCountY - 1;
                                      const waveCount =
                                        this.store.snippetsWaveforms.length ===
                                        1 // AS1-163 req: the chart must have space for at least 2 waveforms.
                                          ? 2
                                          : lastCanvasRow
                                            ? this.store.snippetsWaveforms
                                                .length % 8 || 8
                                            : 8;
                                      return (
                                        <canvas
                                          key={`canvas-${columnIndex}-${rowIndex}`}
                                          ref={(ref) => {
                                            _.set(
                                              this,
                                              `canvasRefs[${columnIndex}][${rowIndex}]`,
                                              ref,
                                            );
                                          }}
                                          width={
                                            columnIndex ===
                                            this.canvasCountX - 1
                                              ? fullChartWidth %
                                                  maxCanvasWidth ||
                                                maxCanvasWidth
                                              : maxCanvasWidth
                                          }
                                          height={
                                            this.chartVerticalSpace * waveCount
                                          }
                                          willreadfrequently="true" // eslint-disable-line react/no-unknown-property
                                        />
                                      );
                                    })}
                                </div>
                              );
                            }),
                        )
                      : null}
                  </div>
                );
              })()}
            </div>
          )
        )}
        <canvas
          className="snapshot"
          ref={(r) => (this.refSnapshot = r)}
          height="400"
          width="400"
        />

        <div className="actions">
          {this.store.posting ? (
            <Loader />
          ) : (
            <>
              <button
                className="btn"
                onClick={this.preview}
                disabled={this.disabledPreview}
              >
                Preview
              </button>
              {this.forcePreview ? null : (
                <button
                  className="btn"
                  onClick={this.save}
                  disabled={this.disabledSave}
                  data-testid="save-button"
                >
                  Save
                </button>
              )}
              <button className="btn secondary" onClick={this.cancel}>
                Cancel
              </button>
            </>
          )}
        </div>

        <style>
          {`.snippet-preview .swal2-content canvas {
                            width: ${this.store.pdfCanvasZoomWidth}px;
                        }`}
        </style>
      </div>
    );
  }

  get toggleAllLeadsDisabled() {
    return this.disabledSave;
  }
  get disabledPreview() {
    return this.store.loadingSnippet || this.store.renderingCharts;
  }
  get disabledSave() {
    return !!(this.disabledPreview || this.store.posting);
  }
  get forcePreview() {
    return (
      config.pm.forceSnippetPreview ||
      (this.props.store.serverPrefs?.pm?.snippetsForcePreview &&
        config.pm.forceSnippetPreview === undefined)
    );
  }

  setupDiscretes() {
    this.store.discretesOnSnippet =
      this.store.snippetDiscretes?.map((d) => {
        const getValue = (i) => {
          const v = d.values[i];
          if (typeof v === "undefined" || v === null) return "--";
          return v;
        };
        let value;
        if (d.viewCategory === "single") {
          value = getValue(0);
        } else if (d.viewCategory === "comp2") {
          value = `${getValue(0)} / ${getValue(1)}`;
        } else if (d.viewCategory === "comp3") {
          value = `${getValue(0)} / ${getValue(1)} (${getValue(2)})`;
        }
        if (d.isEpisodic) {
          value += " " + (d.time || "--:--");
        }

        return {
          title: d.title,
          value,
        };
      }) || [];
  }

  backToTools() {
    this.props.history?.push(`/patient/${this.asmpid}/monitor/tools`);
  }
  handleSelectChange(value) {
    this.store.selectedDiagnoses = value || [];
    this.selectError = false;
  }

  centerCharts() {
    if (this.refCharts && !this.wasCentered) {
      this.refCharts.scrollLeft =
        this.refCharts.scrollWidth / 2 - this.refCharts.clientWidth / 2;
      this.wasCentered = true;
    }
  }
  changeMeasurement(key) {
    return (e) => {
      this.store[key] = e.target.value;
      this.store.userModifiedMeasurements = true;
    };
  }

  renderCharts = async () => {
    await this.renderPromise;
    try {
      this.renderPromise = this.renderChartsAsync();
      return this.renderPromise;
    } catch (e) {
      console.error("Render Failed", e);
    }
  };
  async renderChartsAsync() {
    const [chartStartTime, chartEndTime] = [
      this.store.snippetStartTime,
      this.store.snippetEndTime,
    ];
    const segments = this.store.sliceWaveforms({
      chartStartTime,
      chartEndTime,
    });
    this.compileDcOffsets(segments);
    for (const group of this.store.snippetsWaveforms) {
      const i = this.store.snippetsWaveforms.indexOf(group);
      for (
        let columnIndex = 0;
        columnIndex < this.canvasCountX;
        columnIndex++
      ) {
        const sliceStart = chartStartTime.plus({
          seconds: columnIndex * this.secondsPerCanvas,
        });
        const sliceEnd = sliceStart.plus({ seconds: this.secondsPerCanvas });
        const segments = this.store.sliceWaveforms({
          chartStartTime: sliceStart,
          chartEndTime: sliceEnd,
        });
        const columnCanvases = Array(this.canvasCountY)
          .fill()
          .map((u, rowIndex) => this.canvasRefs[columnIndex][rowIndex]);
        for (const canvas of columnCanvases) {
          const k = columnCanvases.indexOf(canvas);
          if (!canvas) return;
          let ctx = canvas.getContext("2d");
          // clear canvas
          if (i === 0) clearCanvas(ctx);
          let xOffset = columnIndex * this.maxCanvasWidth;
          let yOffset = k * this.chartVerticalSpace * 8;
          this.renderWaveform({
            ctx,
            group,
            segments,
            xOffset,
            yOffset,
            i,
          });
          if (i === 0) {
            this.renderMarkers({ ctx, xOffset });
          }
          this.renderLabel({ ctx, group, i: i - 8 * k });
          await new Promise((resolve) => {
            this.raf = requestIdleCallback(resolve);
          });
        }
      }
    }
    this.renderSnapshot({ segments });
    this.centerCharts();
  }

  renderMarkers({ ctx, xOffset = 0, drawLines }) {
    if (!this.store.measurementMode || !ctx?.canvas) return;
    ctx.setTransform(1, 0, 0, 1, -xOffset, 0);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.font = '600 16px "Open Sans"';
    ctx.fillStyle = "#000";

    if (drawLines) {
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 1;
      ctx.beginPath();
    }
    const labels = [];
    const detectCollision = ({ x, width }) =>
      labels.find((label) => Math.abs(label.x - x) < (label.width + width) / 2);
    this.store.markers.forEach((marker) => {
      if (marker.muted) return;
      let x =
        marker.timestamp &&
        toLuxon(marker.timestamp)
          .diff(toLuxon(this.store.snippetStartTime), "seconds")
          .as("seconds") * this.pixelsPerSecond;
      if (drawLines) {
        ctx.moveTo(x, this.topMargin);
        ctx.lineTo(x, ctx.canvas.height);
      }
      const { width } = ctx.measureText(marker.label);
      while (detectCollision({ x, width })) {
        const label = detectCollision({ x, width });
        x = label.x + (label.width + width) / 2;
      }
      labels.push({ x, width });
      ctx.fillText(marker.label, x, 0);
    });
    if (drawLines) ctx.stroke();
    ctx.resetTransform();
  }

  renderGrid({ ctx, xOffset }) {
    this.renderHorizontalLines({
      ctx,
      resolutionSeconds: 0.04,
      xOffset,
      minor: true,
    });
    this.renderHorizontalLines({
      ctx,
      resolutionSeconds: 0.2,
      xOffset,
    });

    this.renderVerticalLines({ ctx, resolutionSeconds: 0.04, minor: true });
    this.renderVerticalLines({ ctx, resolutionSeconds: 0.2 });
  }
  renderHorizontalLines({ ctx, resolutionSeconds, minor, xOffset }) {
    ctx.beginPath();
    const color = tinyColor("#FE6A52");
    if (minor) color.setAlpha(0.5);
    ctx.strokeStyle = color.toRgbString();
    ctx.lineWidth = 1;
    const pixelDistance = resolutionSeconds * this.pixelsPerSecond;

    for (let y = this.topMargin; y < ctx.canvas.height; y += pixelDistance) {
      ctx.moveTo(xOffset, y);
      ctx.lineTo(xOffset + ctx.canvas.width, y);
    }

    ctx.stroke();
  }
  renderVerticalLines({ ctx, resolutionSeconds, minor }) {
    ctx.beginPath();
    const color = tinyColor("#FE6A52");
    if (minor) color.setAlpha(0.5);
    ctx.strokeStyle = color.toRgbString();
    ctx.lineWidth = 1;

    for (let i = 0; i < this.chartLengthSeconds; i += resolutionSeconds) {
      let x = this.pixelsPerSecond * i;
      ctx.moveTo(x, this.topMargin);
      ctx.lineTo(x, ctx.canvas.height);
    }
    ctx.stroke();
  }
  renderWaveform({
    ctx,
    group,
    segments,
    xOffset = 0,
    yOffset = 0,
    i = 0,
    width = this.fullChartWidth,
  }) {
    if (!group) return;

    ctx.translate(-xOffset, 0);
    ctx.setTransform(1, 0, 0, 1, -xOffset, 0);
    const chartHeight = this.chartHeight;
    let chartVerticalSpace = this.chartVerticalSpace;
    // The input yOffset is the translation for the canvas.
    // The _yOffset is how far down the canvas to start the chart.
    const padding = chartVerticalSpace - chartHeight;
    let _yOffset =
      chartVerticalSpace * i - yOffset + padding + this.chartHeight;

    let yScale = generateYScale({
      group,
      pixelsPerSecond: this.pixelsPerSecond,
      yOffset: _yOffset,
      chartHeight,
    });

    if (i === 0) {
      this.renderGrid({
        ctx,
        group,
        yScale,
        xOffset,
      });
    }
    // Create clipping path
    ctx.save();
    let region = new Path2D();
    region.rect(
      0,
      this.topMargin,
      xOffset + ctx.canvas.width,
      yOffset + ctx.canvas.height,
    );
    ctx.clip(region);

    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.beginPath();
    const dcOffset = this.dcOffsets[group.key];
    let firstPoint, lastRenderedSecond;
    let pixelsPerSecond = this.pixelsPerSecond;

    segments.forEach((segment, i) => {
      let waveSegment = segment.waveData.find((wave) => wave.key === group.key);
      if (waveSegment && waveSegment.data) {
        if (!waveSegment.data.length) {
          firstPoint = false;
          return;
        }
        const segmentTimeStamp = segment.dateTime;
        if (
          lastRenderedSecond &&
          segmentTimeStamp.diff(lastRenderedSecond, "seconds").as("seconds") > 1
        ) {
          firstPoint = false; // leave a gap for non-consecutive segments.
        }
        let pointsPerSecond = waveSegment.data.length;
        let frameTimeToEnd_ms = segmentTimeStamp
          .diff(this.store.snippetEndTime)
          .as("milliseconds");
        let rangeStartMS =
          group.range && i === 0 ? toLuxon(group.range.start).milliseconds : 0;
        yScale = yScale.update(waveSegment);
        waveSegment.data
          .map((y) => (y === null ? null : y - dcOffset))
          .forEach((point, pointIndex) => {
            if (point === null) {
              firstPoint = false;
              return;
            }
            let fractionOfSecond = pointIndex / pointsPerSecond;

            if (pointIndex / pointsPerSecond < rangeStartMS / 1000) {
              return;
            }

            let x =
              (frameTimeToEnd_ms / 1000 + fractionOfSecond) * pixelsPerSecond +
              width;
            if (x < 0) {
              return;
            }
            if (!firstPoint) {
              firstPoint = true;
              ctx.moveTo(x, yScale(point));
            } else {
              ctx.lineTo(x, yScale(point));
            }
          });
        lastRenderedSecond = toLuxon(segmentTimeStamp);
      }
    });
    ctx.stroke();
    ctx.resetTransform();
    ctx.restore();
  }

  compileDcOffsets(segments) {
    this.dcOffsets = this.store.snippetsWaveforms.reduce((offsets, group) => {
      if (group) offsets[group.key] = this.calculateDcOffset(group, segments);
      return offsets;
    }, {});
  }
  calculateDcOffset(group, segments) {
    const rangeCenter = _.mean([group.maxRange, group.minRange]);
    const trueMean = _.mean(
      _.flatten(
        segments
          .map((segment) => segment.waveData.find((d) => d.key === group.key))
          .map((d) => _.get(d, "data", [])),
      ),
    );
    return trueMean - rangeCenter || 0;
  }

  renderLabel({ ctx, group, i }) {
    ctx.resetTransform();

    ctx.font = '600 16px "Open Sans"';
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 3;
    ctx.fillStyle = "#000";
    ctx.textAlign = "left";
    let textArgs = [group.title, 5, this.chartVerticalSpace * (i + 0.5)];
    ctx.strokeText(...textArgs);
    ctx.fillText(...textArgs);
  }

  renderSnapshot({ segments }) {
    if (!this.store.measurementMode) return;
    if (!this.refSnapshot) return;
    let ctx = this.refSnapshot.getContext("2d");

    let timestamps = this.store.markers
      .filter((m) => !m.muted)
      .map((m) => m.timestamp);
    let paddingSeconds = 0.2;
    let minTime = toLuxon(_.min(timestamps)).minus({ seconds: paddingSeconds });
    let maxTime = toLuxon(_.max(timestamps)).plus({ seconds: paddingSeconds });

    let timeOffsetSeconds = minTime
      .diff(this.store.snippetStartTime, "seconds")
      .as("seconds");
    let xOffset = timeOffsetSeconds * this.pixelsPerSecond;
    let durationSeconds = maxTime.diff(minTime, "seconds").as("seconds");
    let canvasSize = durationSeconds * this.pixelsPerSecond;
    canvasSize = Math.max(canvasSize, this.chartVerticalSpace + this.topMargin);
    ctx.canvas.height = canvasSize;
    ctx.canvas.width = canvasSize;

    // This is just for development, so we can see the render a the correct aspect.
    Object.assign(this.refSnapshot.style, {
      height: `${canvasSize}px`,
      width: `${canvasSize}px`,
    });

    this.renderWaveform({
      ctx,
      group: this.store.snippetsWaveforms[0],
      segments,
      xOffset: xOffset,
    });
    this.renderMarkers({ ctx, xOffset, drawLines: true });
  }

  toggleAllLeads() {
    this.store.isAllLeads = !this.store.isAllLeads;
    this.props.store.setPref("pm.snippet.allLeads", this.store.isAllLeads);
    setTimeout(
      () => (this.store.isAllLeadsRender = this.store.isAllLeads),
      200,
    );
  }

  descriptionChange(e) {
    this.store.customDescription = e.target.value;
  }
  preview(e) {
    if (e && e.target.disabled) {
      return;
    }

    let selectedDiagnoses = [];

    if (this.store.selectedDiagnoses) {
      selectedDiagnoses = this.store.selectedDiagnoses;
    }

    const eventDescriptions = selectedDiagnoses.map((d) => d.label);

    if (
      this.props.store.serverPrefs?.pm?.snippetsRequireDescription &&
      !eventDescriptions.length
    ) {
      this.descriptionSelect.focus();
      this.descriptionSelect.state.menuIsOpen = true;
      this.selectError = true;
      this.popupError(
        "The Description is a required field",
        "Description required",
      );
      return;
    }

    this.postSnippet({ preview: true })
      .then((res) => {
        if (!res) throw new Error("No response from server");
        // Either converts the response to a React dom to render into a modal, or stores. the data to be rendered in the
        // main view (for Epic).
        if (config.pm.jpegPreview) {
          if (!res.renderedJpgBytes) {
            throw new Error("No image was returned.");
          }
          return (
            <div className="jpeg-preview">
              <img
                alt="chart"
                src={b64toBlobUrl(res.renderedJpgBytes, "image/jpeg")}
              />
            </div>
          );
        }
        if (!res.renderedPdfBytes?.length)
          throw new Error("No PDF was returned from server.");
        if (config.epic) {
          // EPIC browser can't handle PDF.js in a modal.
          this.store.pdf = b64toBlobUrl(
            res.renderedPdfBytes,
            "application/pdf",
          );
          return null;
        }
        const b64url = b64toBlobUrl(res.renderedPdfBytes, "application/pdf");
        return <iframe className="pdf-preview" src={b64url + "#toolbar=0"} />;
      })
      .catch(async (err) => {
        await this.popupError(err, "request failed");
        err.cause?.resolve?.();
      })
      .finally(() => (this.store.posting = false))
      .then((content) => {
        content &&
          reactSwal
            .fire({
              html: content,
              showCancelButton: true,
              cancelButtonText: "Back",
              confirmButtonText: "Save",
              customClass: "snippet-preview",
            })
            .then((result) => {
              if (result?.value) this.save();
            })
            .catch((e) => this.popupError(e, "preview modal failed"));
      })
      .catch((e) => this.popupError(e, "preview failed"));
  }

  async postSnippet({ preview }) {
    this.store.posting = true;

    await this.renderPromise;
    let snippetDetails = _.pick(this.props.store.patient, [
      "firstName",
      "lastName",
      "mrn",
      "gender",
    ]);

    let selectedDiagnoses = [];

    if (this.store.selectedDiagnoses) {
      selectedDiagnoses = this.store.selectedDiagnoses;
    }

    const eventDescriptions = selectedDiagnoses.map((d) => d.label);

    if (
      this.props.store.serverPrefs?.pm?.snippetsRequireDescription &&
      !eventDescriptions.length
    ) {
      this.descriptionSelect.focus();
      this.descriptionSelect.state.menuIsOpen = true;
      this.selectError = true;
      this.popupError(
        "The Description is a required field",
        "Description required",
      );
      return;
    }

    const SNIPPET_DOCUMENT_TYPE_ID = {
      ADHOC: 0,
      BASELINE: 1,
      SHIFTCHANGE: 2,
      EVENT: 3,
    };

    const snippetDocumentTypeId = this.isSnippetWorklist
      ? SNIPPET_DOCUMENT_TYPE_ID.SHIFTCHANGE
      : this.isBaselineWorklist
        ? SNIPPET_DOCUMENT_TYPE_ID.BASELINE
        : this.store.selectedDocTypeOption.value;

    Object.assign(snippetDetails, {
      dobFormatted: this.props.store.patient.dateOfBirth,
      dob: convertFormattedDateToISO(this.props.store.patient.dateOfBirth),
      hr: this.store.HR,
      pr: formatNumber(this.store.PR, this.intervalPlaceholder),
      qrs: formatNumber(this.store.QRS, this.intervalPlaceholder),
      qt: formatNumber(this.store.QT, this.intervalPlaceholder),
      qtc: config.pm.qtc ? formatNumber(this.store.QTc) : undefined,
      discretes: this.store.discretesOnSnippet,
      isDualLead: !!this.props.store.serverPrefs?.pm?.snippetsDualLead,
      isAllLeads: this.store.isAllLeads,
      lead0: this.store.selectedWaveform1key,
      lead1: this.store.selectedWaveform2key || "",
      siteName: config.siteName,
      unit: _.get(
        this.props.store.patient.moduleSpecificData.find(
          (specs) =>
            specs.moduleId.toString() ===
            MODULE_ID.PATIENT_MONITORING.toString(),
        ),
        "unit",
        "",
      ),
      snippetBeginDateTime: this.store.snippetStartTime,
      snippetEndDateTime: this.store.snippetEndTime,
      userModifiedMeasurements: !!this.store.userModifiedMeasurements,
      eventDescription: `${eventDescriptions.join(", ")}`,
      snippetDocumentTypeId,
      eventDescriptionDetails: selectedDiagnoses.map((d) => ({
        statementCode: d.value,
        statementText: d.label,
      })),
      location: _.get(
        this.props.store.patient.moduleSpecificData.find((d) => d.location),
        "location",
      ),
    });

    let snippetRawData;
    if (
      config.pm.snippetRawData &&
      this.store.measurementMode &&
      this.store.snippetRawData
    ) {
      let waveGroup = this.store.config.waveGroups.find(
        (g) => g.key === this.store.selectedWaveform1key,
      );
      waveGroup = {
        ...waveGroup,
        disabled: undefined,
      };
      const userPQRSTIndex = this.store.markers.map((m) => {
        if (m.muted) {
          return null;
        } else {
          const rawTimestamp = toLuxon(m.timestamp);
          const diffFromWaveStartTime = rawTimestamp.diff(
            this.store.snippetRawData.waveStartTime,
          );
          const diffMillis = diffFromWaveStartTime.as("milliseconds");
          const PQRST_INDEX =
            (diffMillis * this.store.snippetRawData.sampleRate) / 1000;
          return PQRST_INDEX;
        }
      });

      // put the R index back in.
      userPQRSTIndex.splice(
        2,
        0,
        this.store.snippetRawData.defaultPQRSTIndex[2],
      );
      const patient = this.props.store.patient;

      snippetRawData = Object.assign(this.store.snippetRawData, {
        userPQRSTIndex,
        snippetTimestamp: this.store.snippetStartTime,
        waveGroup,
        clientVersion: packageInfo.version,
        patient: {
          age: patient.age,
          asmpid: patient.asmpid,
          unit: _.get(
            patient.moduleSpecificData.find((d) => d.unit),
            "unit",
          ),
          location: _.get(
            patient.moduleSpecificData.find((d) => d.location),
            "location",
          ),
        },
        buildDate: packageInfo.buildDate,
        siteId: config.siteid,
        siteName: config.siteName,
        user: { name: this.props.store.authStore.username },
        eventDescription: snippetDetails.eventDescription,
        eventDescriptionDetails: snippetDetails.eventDescriptionDetails,
      });
    }

    let snippetLead = Array(this.canvasCountX)
      .fill()
      .map((u, i) => {
        let startTime = this.store.snippetStartTime.plus({
          seconds: this.secondsPerCanvas * i,
        });
        let endTime =
          i === this.canvasCountX - 1
            ? this.store.snippetEndTime
            : startTime.plus({ seconds: this.secondsPerCanvas });
        return Array(this.canvasCountY)
          .fill()
          .map((u, j) => {
            const sourceCanvas = this.canvasRefs[i][j];
            if (!sourceCanvas) return null;
            const tempCanvas = document.createElement("canvas");
            tempCanvas.setAttribute("height", sourceCanvas.height);
            tempCanvas.setAttribute("width", this.maxCanvasWidth);
            const tempCtx = tempCanvas.getContext("2d");
            tempCtx.drawImage(sourceCanvas, 0, 0);

            return {
              leadImage: tempCanvas
                .toDataURL()
                .replace("data:image/png;base64,", ""),
              beginTime: this.time(startTime),
              endTime: this.time(endTime),
              beginDate: this.date(startTime),
              endDate: this.date(endTime),
            };
          });
      });
    snippetLead = _.flatten(snippetLead);

    if (config.epic) {
      if (preview) {
        this.cachedSnippetLead = snippetLead;
      } else if (this.cachedSnippetLead) {
        snippetLead = this.cachedSnippetLead;
      }
    }

    if (!snippetLead || !snippetLead[0]) {
      const description = "Image data missing";
      reactSwal.fire({
        icon: "error",
        text: description,
      });
      return Promise.reject({ description }).finally(() => {
        this.store.posting = false;
      });
    }

    const model = {
      snippetDetails,
      snippetLead,
      snippetRawData,
    };

    if (this.store.measurementMode) {
      model.snippetSnapshot = this.refSnapshot
        .toDataURL()
        .replace("data:image/png;base64,", "");
    }

    const query = {
      snippetApprovalOverride:
        this.props.store.prefs?.userPreferences?.client?.pm
          ?.preventSnippetAutoApproval || undefined,
    };
    if (!preview) {
      query.snippetworklistId =
        this.props.store.patient.moduleSpecifics?.snippetWorklistId || "";
    }

    const postMethod = preview
      ? this.store.getSnippetPreview
      : this.store.saveSnippet;
    return postMethod({ model, query }).finally(() => {
      this.store.posting = false;
    });
  }
  save(e) {
    if (e && e.target.disabled) {
      return;
    }

    if (config.pm.training) {
      console.log("In training mode, did not actually save snippet.");
      this.saveComplete();
      return;
    }

    this.postSnippet({})
      .then((data) => {
        if (!data) throw new Error("No data returned from server");
        if (data.error) throw new Error(data.error);
        this.saveComplete();
      })
      .catch(async (err) => {
        if (config.debug || err.cause?.resolve) {
          await this.popupError(err, "save failed");
          err.cause?.resolve?.();
        }
      })
      .finally(() => (this.store.posting = false));

    this.store.logSnippetCreation({ stage: "completed" });
  }
  saveComplete() {
    this.saved = true;
    // clear measure mode markers
    if (config.debug) alert("Snippet Saved");
    reactSwal.fire("Snippet Saved");
    this.props.store.hideMainNav = false;
    this.props.store.monitorStore = undefined;
    if (this.isSnippetWorklist || this.isBaselineWorklist) {
      this.props.history.push("/patients");
    } else {
      this.props.history.push(
        `/patient/${
          this.asmpid
        }/monitor/history/${this.store.chartEndTime.toISO()}`,
      );
    }
  }
  cancel() {
    Object.assign(this.store, this.backup);
    this.store.chartEndTime = this.store.toolsChartEndTime;
    this.props.store.hideMainNav = false;
    this.props.history.push("tools");
    reactSwal.fire({
      title: '<span style="color: #b93333;">Canceled: Snippet not saved<span>',
      icon: "error",
      showCancelButton: false,
      timer: 5000,
    });
  }
  popupError(message, title = "Error") {
    return reactSwal.fire({
      title: title,
      text: message,
      icon: "error",
    });
  }
}

export default inject("store")(observer(CreateStatement));

export function clearCanvas(ctx) {
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
}

//https://stackoverflow.com/questions/16245767/creating-a-blob-from-a-base64-string-in-javascript
export function b64toBlobUrl(b64Data, contentType = "", sliceSize = 1024) {
  const byteCharacters = atob(b64Data.replace(/\s/g, ""));
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  const blobUrl = URL.createObjectURL(blob);
  return blobUrl;
}

function convertFormattedDateToISO(formattedDate) {
  if (!formattedDate) {
    return null;
  }

  // Try parsing with "MM/dd/yyyy" format
  let parsedDate = parse(formattedDate, "MM/dd/yyyy", new Date());

  // If parsing fails, try with "dd/MM/yyyy" format
  if (!isValid(parsedDate)) {
    parsedDate = parse(formattedDate, "dd/MM/yyyy", new Date());
  }

  // If parsing fails, try with "yyyy/MM/dd" format
  if (!isValid(parsedDate)) {
    parsedDate = parse(formattedDate, "yyyy/MM/dd", new Date());
  }

  // If parsing is still invalid, throw an error
  if (!isValid(parsedDate)) {
    return null;
  }

  // Format the parsed date to the desired output format
  const isoDate = format(parsedDate, "yyyy-MM-dd");

  return isoDate;
}

function IntervalInput({ interval, parent }) {
  const defaultValue = formatNumber(parent.store[interval]);

  return (
    <label>
      {interval} <span className="unit">(s)</span>:
      <input
        step={0.01}
        defaultValue={defaultValue}
        type="number"
        onChange={parent.changeMeasurement(interval)}
        readOnly={parent.editMeasurements === false}
        placeholder={parent.intervalPlaceholder}
      />
    </label>
  );
}
